import { useCallback, useMemo, useState } from 'react';

import FilterListIcon from '@mui/icons-material/FilterList';
import { ToolbarButton } from '@mui/x-data-grid';
import { FilterPanelTrigger, Toolbar } from '@mui/x-data-grid-premium';
import { GridValidRowModel } from '@mui/x-data-grid/models/gridRows';
import noop from 'lodash/noop';

import {
  Alert,
  Badge,
  Box,
  Button,
  DataGridPremium,
  GridLogicOperator,
  GridPaginationModel,
  Stack,
  Tooltip,
  Typography,
} from '@sbv-clients/ui-shared';
import { getApiErrorMessage } from '@sbv-clients/util-misc';

import { useAdminFacilitiesActiveEmployeesGet } from '../../../services/admin-facility';
import useColumns from './use-columns';
import { useFiltersModel } from './use-filters-model';

interface SbvProDatGridProps {
  facilityId: string;
}

function CustomToolbar({ isRefreshing, onRefresh }: any) {
  return (
    <Box display="flex" justifyContent="space-between" alignItems="center">
      <Toolbar style={{ minHeight: '36px', border: 'none' }}>
        <Tooltip title="Filters">
          <FilterPanelTrigger
            render={(props, state) => (
              <ToolbarButton {...(props as any)} color="default">
                <Badge
                  badgeContent={state.filterCount}
                  color="primary"
                  variant="dot"
                >
                  <FilterListIcon fontSize="small" />
                </Badge>
              </ToolbarButton>
            )}
          />
        </Tooltip>
      </Toolbar>
      <Button
        size="small"
        disabled={isRefreshing}
        onClick={onRefresh}
        variant="contained"
      >
        {isRefreshing ? '...Refreshing' : 'Refresh'}
      </Button>
    </Box>
  );
}

const getRowId = (item: GridValidRowModel) => item.pkEmployeeId as number;

const FacilityUsersGrid = ({ facilityId }: SbvProDatGridProps) => {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });

  const { filterModel, setFilterModel, apiFilters } = useFiltersModel();
  const columns = useColumns(facilityId);
  const { data, isLoading, isRefetching, refetch, isError, error } =
    useAdminFacilitiesActiveEmployeesGet(
      {
        facilityId,
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        ...apiFilters,
      },
      { keepPreviousData: true }
    );

  const employees = data?.data;
  const rowCount = data?.rowCount ?? 0;

  const onRefresh = useCallback(async () => {
    await refetch().catch(noop);
  }, [refetch]);

  const noRowsOverlay = useMemo(() => {
    return !isError
      ? () => (
          <Stack height={'85%'} justifyContent={'center'} alignItems={'center'}>
            <Typography>No data found!</Typography>
          </Stack>
        )
      : () => (
          <Stack height={'85%'} justifyContent={'center'} alignItems={'center'}>
            <Alert color={'error'} variant={'outlined'}>
              <Typography>{getApiErrorMessage(error)}</Typography>
            </Alert>
          </Stack>
        );
  }, [error, isError]);

  return (
    <>
      <DataGridPremium
        rows={employees ?? []}
        columns={columns}
        getRowId={getRowId}
        filterMode="server"
        paginationMode="server"
        pagination
        pageSizeOptions={[25, 50, 75, 100]}
        hideFooter={isLoading}
        rowCount={rowCount}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        loading={isLoading || isRefetching}
        filterModel={filterModel}
        onFilterModelChange={setFilterModel}
        filterDebounceMs={1000}
        showToolbar
        slots={{
          toolbar: CustomToolbar,
          noRowsOverlay,
        }}
        slotProps={{
          filterPanel: { logicOperators: [GridLogicOperator.And] },
          toolbar: { isRefreshing: isRefetching, onRefresh } as any,
        }}
        sx={{
          '& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer':
            {
              visibility: 'hidden',
            },
        }}
      />
    </>
  );
};

export default FacilityUsersGrid;
